import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import Cookie from 'js-cookie';
import switchColors from '../utils/switchColors';

import {
  SET_AUTH_STATE,
  SET_AUTH_STATUS,
  SET_LOCALE,
  SET_THEME,
  SET_SUBSCRIPTION,
  SET_AUTH_CHECK_STATUS,
  SET_LOGIN_PAGE_ENABLED
} from './mutation-types';

import {
  AUTH_CHECK,
  AUTH_LOGIN,
  AUTH_LOGOUT,
  UPDATE_LOCALE,
  UPDATE_THEME
} from './action-types';

import {
  ERROR_AUTH_STATUS,
  PENDING_AUTH_STATUS,
  SUCCESS_AUTH_STATUS,
  UNDEFINED_AUTH_STATUS
} from './auth-status';

Vue.use(Vuex);

const debug = process.env.NODE_ENV !== 'production';
const DEV_SESSION_COOKIE_NAME = 'DEV_SESSION';

const isMspWr = process.env.VUE_APP_MSP_MODE === 'true';

export default new Vuex.Store({
  state: {
    locale: '',
    theme: false,
    authenticated: false,
    authStatus: UNDEFINED_AUTH_STATUS,
    subscription: isMspWr ? 'WRE' : 'WRH',
    authCheckStatus: UNDEFINED_AUTH_STATUS,
    loginPageEnabled: true
  },
  getters: {
    locale: state => state.locale,
    isAuthenticated: state => state.authenticated,
    authStatus: state => state.authStatus,
    subscription: state => state.subscription,
    authCheckStatus: state => state.authCheckStatus,
    isLoginPageEnabled: state => state.loginPageEnabled
  },
  mutations: {
    [SET_LOCALE](state, locale) {
      state.locale = locale;
    },
    [SET_THEME](state, theme) {
      state.theme = theme;
    },
    [SET_AUTH_STATE](state, authState) {
      state.authenticated = authState;
    },
    [SET_AUTH_STATUS](state, authStatus) {
      state.authStatus = authStatus;
    },
    [SET_SUBSCRIPTION](state, subscription) {
      state.subscription = subscription;
    },
    [SET_AUTH_CHECK_STATUS](state, authCheckStatus) {
      state.authCheckStatus = authCheckStatus;
    },
    [SET_LOGIN_PAGE_ENABLED](state, loginPageEnabled) {
      state.loginPageEnabled = loginPageEnabled;
    }
  },
  actions: {
    [UPDATE_LOCALE]({ commit }, locale) {
      commit(SET_LOCALE, locale);
    },
    [UPDATE_THEME]({ commit }, theme) {
      import(`@uikit/ui-kit/colors/js/${theme === 'default' ? '' : 'legacy/'}${theme}.js`)
        .then(({ default: colors }) => {
          switchColors(colors);
        }).finally(() => {
          commit(SET_THEME, true);
        });
    },
    async [AUTH_CHECK]({ commit }) {
      if (debug) {
        const devSessionCookie = Cookie.get(DEV_SESSION_COOKIE_NAME);
        const isAuthenticated = devSessionCookie !== 'undefined' && devSessionCookie === 'true';
        if (isMspWr) {
          commit(SET_SUBSCRIPTION, 'WRE');
        } else if (isAuthenticated) {
          commit(
            SET_SUBSCRIPTION,
            window.location.href.toLowerCase().includes('/enterprise') ? 'WRE' : 'WRH'
          );
        }
        commit(SET_AUTH_STATE, isAuthenticated);
        commit(SET_LOGIN_PAGE_ENABLED, true);
      } else {
        commit(SET_AUTH_CHECK_STATUS, PENDING_AUTH_STATUS);
        await axios.get('/session')
          .then(function (response) {
            const isAuthenticated = response.data.authenticated;

            if (isMspWr) {
              commit(SET_SUBSCRIPTION, 'WRE');
            } else if (isAuthenticated) {
              commit(SET_SUBSCRIPTION, response.data.subscription);
            } else {
              commit(
                SET_SUBSCRIPTION,
                window.location.href.toLowerCase().includes('/enterprise') ? 'WRE' : 'WRH'
              );
            }

            commit(SET_AUTH_STATE, isAuthenticated);
            commit(SET_LOGIN_PAGE_ENABLED, response.data.loginPageEnabled);
          }).finally(function () {
            commit(SET_AUTH_CHECK_STATUS, UNDEFINED_AUTH_STATUS);
          });
      }
    },
    async [AUTH_LOGIN]({ commit, dispatch }, loginForm) {
      commit(SET_AUTH_STATUS, PENDING_AUTH_STATUS);

      if (debug) {
        await setTimeout(function () {
          if (loginForm.password === '123') {
            if (isMspWr) {
              commit(SET_SUBSCRIPTION, 'WRE');
            } else {
              commit(
                SET_SUBSCRIPTION,
                window.location.href.toLowerCase().includes('/enterprise') ? 'WRE' : 'WRH'
              );
            }

            commit(SET_AUTH_STATE, true);
            commit(SET_AUTH_STATUS, SUCCESS_AUTH_STATUS);
            Cookie.set(DEV_SESSION_COOKIE_NAME, 'true', { expires: 365 });
          } else {
            commit(SET_AUTH_STATE, false);
            commit(SET_AUTH_STATUS, ERROR_AUTH_STATUS);
          }
        }, 1500);
      } else {
        await axios.post('/session', loginForm)
          .then(function (response) {
            if (isMspWr) {
              commit(SET_SUBSCRIPTION, 'WRE');
            } else {
              commit(
                SET_SUBSCRIPTION,
                window.location.href.toLowerCase().includes('/enterprise') ? 'WRE' : 'WRH'
              );
            }

            commit(SET_AUTH_STATE, true);
            commit(SET_AUTH_STATUS, SUCCESS_AUTH_STATUS);
          })
          .catch(function (error) {
            if (error.response) {
              if (error.response.status === 403) {
                commit(SET_AUTH_STATE, false);
                commit(SET_AUTH_STATUS, ERROR_AUTH_STATUS);
              } else if (error.response.status !== 200) {
                commit(SET_AUTH_STATE, false);
                commit(SET_AUTH_STATUS, UNDEFINED_AUTH_STATUS);
              }
            }
          });
      }
    },
    async [AUTH_LOGOUT]({ commit, dispatch }) {
      if (debug) {
        commit(SET_AUTH_STATE, false);
        Cookie.set(DEV_SESSION_COOKIE_NAME, 'false', { expires: 365 });
        window.location.replace(`${window.location.href}`);
      } else {
        await axios.delete('/session')
          .then(function (response) {
            commit(SET_AUTH_STATE, false);
          }).finally(function () {
            window.location.replace(`${window.location.href}`);
          });
      }
    }
  },
  modules: {},
  strict: debug
});
